import { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  ArrowLeft,
  UploadCloud,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Users,
} from 'lucide-react';
import type { ImportMember } from '@sharecost/shared';
import { useGroupDetail } from '@/hooks/useGroups';
import { useAuth } from '@/contexts/AuthContext';
import { usePremium } from '@/hooks/usePremium';
import { isPremiumTier, isGroupPremium } from '@/lib/premium';
import { PremiumPaywall } from '@/components/common/PremiumPaywall';
import { useImport } from '@/hooks/useImport';
import { useTranslation } from 'react-i18next';

// Translated at render time (module-scope constants run before the bundles load)
const WARNING_LABEL_KEYS: Record<string, string> = {
  unparseable_amount: 'web:importExpensesPage.warnUnreadableAmount',
  unknown_currency: 'web:importExpensesPage.warnMissingCurrency',
  unparseable_date: 'web:importExpensesPage.warnUnreadableDate',
  split_sum_mismatch: 'web:importExpensesPage.warnSplitsMismatch',
  missing_payer: 'web:importExpensesPage.warnNoPayer',
  no_participants: 'web:importExpensesPage.warnNoParticipants',
};

export function ImportExpensesPage() {
  const { t } = useTranslation();
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: group } = useGroupDetail(groupId);
  const { tier } = usePremium();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const members = useMemo<ImportMember[]>(
    () =>
      (group?.members ?? []).map((m) => ({
        id: m.user.id,
        email: m.user.email ?? null,
        displayName: m.user.display_name,
      })),
    [group],
  );

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) map.set(m.id, m.displayName);
    return map;
  }, [members]);

  const canImport = useMemo(
    () => isPremiumTier(tier) || isGroupPremium(group?.members ?? []),
    [tier, group],
  );

  const {
    stage,
    preview,
    fileName,
    progress,
    result,
    error,
    start,
    commit,
    reset,
    setTokenVirtual,
    toggleDraft,
  } = useImport({
    groupId: groupId ?? '',
    groupCurrency: group?.currency_code ?? 'USD',
    createdBy: user?.id ?? '',
    members,
  });

  const handleFile = (file: File | undefined) => {
    if (file) start(file);
  };

  const includedCount = useMemo(
    () => preview?.drafts.filter((d) => d.include).length ?? 0,
    [preview],
  );
  const settlementCount = useMemo(
    () => preview?.drafts.filter((d) => d.include && d.row.kind === 'settlement').length ?? 0,
    [preview],
  );
  const duplicateCount = useMemo(
    () => preview?.drafts.filter((d) => d.duplicate).length ?? 0,
    [preview],
  );

  if (!group) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <button
        onClick={() => navigate(`/groups/${groupId}`)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> {t('settlements:empty.backToGroup')}
      </button>

      <h1 className="text-xl font-semibold text-text-primary">{t('web:importExpensesPage.importExpenses')}</h1>
      <p className="mt-1 text-sm text-text-tertiary">
        {t('web:importExpensesPage.uploadACsvOrExcelExport')} <span className="font-medium text-text-secondary">{group.name}</span>.
      </p>

      {/* Upload */}
      {(stage === 'idle' || stage === 'error') && (
        <div className="relative mt-6">
          {!canImport && (
            <PremiumPaywall
              feature="Import is a Premium feature"
              description="Importing expenses from other apps requires ShareCost Premium."
            />
          )}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (canImport) handleFile(e.dataTransfer.files?.[0]);
            }}
            className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
              dragging ? 'border-brand-600 bg-brand-600/5' : 'border-border-primary'
            }`}
          >
            <UploadCloud className="h-10 w-10 text-text-tertiary" />
            <p className="mt-3 text-sm font-medium text-text-primary">
              {t('web:importExpensesPage.dragAFileHereOr')}
            </p>
            <button
              disabled={!canImport}
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('web:importExpensesPage.chooseFile')}
            </button>
            <p className="mt-3 text-xs text-text-tertiary">{t('web:importExpensesPage.csvXlsxOrXls')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
            />
          </div>

          {stage === 'error' && error && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Processing */}
      {(stage === 'parsing' || stage === 'detecting') && (
        <div className="mt-10 flex flex-col items-center gap-3 py-10 text-center">
          <Loader2 className="h-7 w-7 animate-spin text-brand-600" />
          <p className="text-sm text-text-secondary">
            {stage === 'parsing' ? t('web:importExpensesPage.readingTheFile') : t('web:importExpensesPage.analyzingCategorizingWithAi')}
          </p>
        </div>
      )}

      {/* Preview */}
      {stage === 'preview' && preview && (
        <div className="mt-6 space-y-6">
          <div className="rounded-xl border border-border-primary p-4">
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <FileSpreadsheet className="h-4 w-4 shrink-0" />
              {t('web:importExpensesPage.file')} <span className="truncate font-medium text-text-primary">{fileName}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span className="text-text-secondary">
                {t('web:importExpensesPage.toImport', { count: includedCount })}
              </span>
              {settlementCount > 0 && (
                <span className="text-text-tertiary">{t('web:importExpensesPage.inclSettlements', { count: settlementCount })}</span>
              )}
              {duplicateCount > 0 && (
                <span className="text-text-tertiary">{t('web:importExpensesPage.possibleDuplicates', { count: duplicateCount })}</span>
              )}
            </div>
          </div>

          {/* People mapping */}
          <div>
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-text-primary">
              <Users className="h-4 w-4" /> {t('web:importExpensesPage.people')}
            </h2>
            <div className="mt-2 divide-y divide-border-primary rounded-xl border border-border-primary">
              {preview.tokens.map((token) => (
                <div key={token.token} className="flex items-center justify-between px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-text-primary">{token.token}</p>
                    {token.memberId ? (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                        {t('web:importExpensesPage.matched')} {memberNameById.get(token.memberId) ?? t('web:importExpensesPage.member')} ({token.matchType})
                      </p>
                    ) : (
                      <p className="text-xs text-text-tertiary">{t('web:importExpensesPage.notInThisGroup')}</p>
                    )}
                  </div>
                  {!token.memberId && (
                    <label className="flex shrink-0 items-center gap-2 text-xs text-text-secondary">
                      <input
                        type="checkbox"
                        checked={token.createVirtual}
                        onChange={(e) => setTokenVirtual(token.token, e.target.checked)}
                      />
                      {t('web:importExpensesPage.addAsNewMember')}
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div>
            <h2 className="text-sm font-semibold text-text-primary">{t('web:importExpensesPage.transactions')}</h2>
            <div className="mt-2 max-h-80 divide-y divide-border-primary overflow-y-auto rounded-xl border border-border-primary">
              {preview.drafts.map((d) => (
                <div
                  key={d.index}
                  className={`flex items-center gap-3 px-4 py-2.5 ${
                    d.include ? '' : 'opacity-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={d.include}
                    onChange={() => toggleDraft(d.index)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-text-primary">
                      {d.row.description || d.row.categoryName || t('web:importExpensesPage.importedExpense')}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      {(d.row.dateIso ?? '').slice(0, 10) || t('common:noDate')} ·{' '}
                      {d.row.splits.length > 0
                        ? t('web:importExpensesPage.participants', { count: d.row.splits.length })
                        : t('web:importExpensesPage.splitEqually')}
                      {d.row.kind === 'expense' &&
                        ` · ${d.categoryLabel ?? t('web:importExpensesPage.uncategorized')}`}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {d.row.kind === 'settlement' && (
                        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          settlement
                        </span>
                      )}
                      {d.duplicate && (
                        <span className="rounded bg-surface-tertiary px-1.5 py-0.5 text-[10px] text-text-tertiary">
                          {t('web:importExpensesPage.possibleDuplicate')}
                        </span>
                      )}
                      {d.row.warnings.map((w) => (
                        <span
                          key={w}
                          className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                        >
                          {t(WARNING_LABEL_KEYS[w]) ?? w}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-sm font-medium text-text-primary">
                    {d.row.amount != null ? d.row.amount.toFixed(2) : '—'}{' '}
                    <span className="text-xs text-text-tertiary">
                      {d.row.currencyCode ?? group.currency_code}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={reset}
              className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-secondary"
            >
              {t('common:button.cancel')}
            </button>
            <button
              onClick={commit}
              disabled={includedCount === 0}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('web:importExpensesPage.importItems', { count: includedCount })}
            </button>
          </div>
        </div>
      )}

      {/* Committing */}
      {stage === 'committing' && (
        <div className="mt-10 flex flex-col items-center gap-3 py-10 text-center">
          <Loader2 className="h-7 w-7 animate-spin text-brand-600" />
          <p className="text-sm text-text-secondary">
            {t('web:importExpensesPage.importing')} {progress.done}/{progress.total}
          </p>
        </div>
      )}

      {/* Done */}
      {stage === 'done' && result && (
        <div className="mt-10 flex flex-col items-center gap-3 py-10 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          <p className="text-sm font-medium text-text-primary">
            {t('web:importExpensesPage.importedExpenses', { count: result.expenses })}
            {result.settlements > 0 && ` ${t('web:importExpensesPage.andSettlements', { count: result.settlements })}`}
          </p>
          {result.failures.length > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {t('web:importExpensesPage.rowsCouldNotBeImported', { count: result.failures.length })}
            </p>
          )}
          <button
            onClick={() => navigate(`/groups/${groupId}`)}
            className="mt-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white"
          >
            {t('settlements:empty.backToGroup')}
          </button>
        </div>
      )}
    </div>
  );
}
